import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';

import { IndexDTO } from '@/types/api-types';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

interface AddIndexTableProps {
  data: IndexDTO[];
  onEdit: (index: IndexDTO) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

const SKELETON_ROWS_COUNT = 5;

export function AddIndexTable({
  data,
  onEdit,
  onDelete,
  isLoading,
}: AddIndexTableProps) {
  const isMobile = useIsMobile();

  const columns: ColumnDef<IndexDTO>[] = isMobile
    ? [
        {
          accessorKey: 'name',
          header: 'Індекс',
          size: 100,
        },
        {
          accessorKey: 'insurance_area',
          header: 'Напрямок',
          size: 150,
        },
        {
          accessorKey: 'insurance_object_type',
          header: 'Тип ОС',
          size: 150,
        },
        {
          id: 'actions',
          header: 'Дія',
          size: 100,
          cell: ({ row }) => {
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(row.original)}>
                    Редагувати
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(row.original.uuid)}
                    className="text-destructive"
                  >
                    Видалити
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          },
        },
      ]
    : [
        {
          accessorKey: 'name',
          header: 'Індекс',
          size: 100,
        },
        {
          accessorKey: 'description',
          header: 'Опис',
          size: 300,
        },
        {
          accessorKey: 'insurance_area',
          header: 'Напрямок',
          size: 150,
        },
        {
          accessorKey: 'insurance_object_type',
          header: 'Тип ОС',
          size: 150,
        },
        {
          id: 'actions',
          header: 'Дія',
          size: 100,
          cell: ({ row }) => {
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(row.original)}>
                    Редагувати
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(row.original.uuid)}
                    className="text-destructive"
                  >
                    Видалити
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          },
        },
      ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    filterFns: {
      exact: (row, columnId, value): boolean => {
        const cellValue = String(row.getValue(columnId) || '').toLowerCase();
        const filterValue = String(value).toLowerCase();
        return filterValue === '' || cellValue === filterValue;
      },
    },
  });

  const TableHeader = () => (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} className="sticky top-0 z-10 bg-white">
          {headerGroup.headers.map((header) => (
            <th
              key={header.id}
              style={{ width: header.getSize() }}
              className="border-b border-separator pb-4 pt-4 text-left font-semibold text-primary first:pl-8 last:pr-8 last:text-right"
            >
              {!header.isPlaceholder &&
                flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );

  const TableRow = ({ row }: { row: any }) => (
    <tr className="h-[70px]">
      {row.getVisibleCells().map((cell: any) => (
        <td
          key={cell.id}
          className="border-b border-separator text-primary first:pl-8 last:pr-8 last:text-right"
          style={{ width: cell.column.getSize() }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );

  const SkeletonRows = () => (
    <>
      {Array.from({ length: SKELETON_ROWS_COUNT }).map((_, index) => (
        <tr key={`skeleton-${index}`} className="h-[70px]">
          {columns.map((_, cellIndex) => (
            <td
              key={`skeleton-cell-${cellIndex}`}
              className="border-b border-separator first:pl-8 last:pr-8"
            >
              <Skeleton className="h-[30px] w-[80%]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="rounded-md">
      <div className={cn('overflow-auto', 'h-full')}>
        <table className="w-full border-separate border-spacing-0">
          <TableHeader />
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : (
              table
                .getRowModel()
                .rows.map((row) => <TableRow key={row.id} row={row} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
